import React from "react";
import { Link } from "react-router-dom";
import {
  Trophy,
  Users,
  ShieldCheck,
  Sparkles,
  Flag,
  Heart,
  Compass,
  MapPin,
  Calendar,
} from "lucide-react";
import SoccerBall from "@/components/SoccerBall";
import SEO from "@/components/SEO";
import { useSiteSettings } from "@/hooks/useSiteSettings";

/**
 * About — Gil's story, told in his own voice. Written to be read, not scanned.
 * The portrait card uses `hero_image_url` from Website Settings when the owner
 * uploads a photo; otherwise a clean typographic fallback keeps the layout
 * intact without a "coming soon" chip.
 */
export default function About() {
  const settings = useSiteSettings();
  const name = settings.business_name || "Gil Maakaroun";
  return (
    <div data-testid="about-page">
      <SEO
        title={`About ${name}`}
        description={`The story of ${name} — from finishing his last Panini World Cup album in 1998 to organizing South Florida's biggest sticker trading events today.`}
      />

      {/* Hero */}
      <section className="hero-black">
        <div className="container-gm py-14 md:py-20 relative">
          <span className="sticker sticker-yellow" style={{ background: "var(--panini-yellow)" }}>ABOUT</span>
          <h1 className="h-display text-white text-5xl md:text-7xl mt-6 max-w-3xl" data-testid="about-title">
            One kid.<br />
            One album.<br />
            <span className="text-[var(--panini-yellow)]">Twenty-eight years later.</span>
          </h1>
          <p className="text-white/85 mt-6 max-w-2xl text-base md:text-lg leading-relaxed">
            The story behind {name} — and why South Florida&apos;s Panini
            community is growing faster than anyone expected.
          </p>
        </div>
        <div className="field-line" aria-hidden />
      </section>

      {/* Portrait + intro */}
      <section className="container-gm py-16 md:py-24" data-testid="about-who">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="rounded-3xl border-4 border-[var(--ink)] bg-[var(--panini-yellow)] p-4 md:p-6 shadow-[10px_10px_0_var(--panini-red)] relative">
              {settings.hero_image_url ? (
                <img
                  src={settings.hero_image_url}
                  alt={name}
                  className="w-full aspect-square rounded-2xl border-2 border-[var(--ink)] object-cover"
                  data-testid="about-portrait"
                />
              ) : (
                <div className="w-full aspect-square rounded-2xl border-2 border-[var(--ink)] bg-white flex flex-col items-center justify-center text-center p-6" data-testid="about-portrait-fallback">
                  <SoccerBall size={60} strokeColor="#0A0A0A" />
                  <div className="kicker kicker-red mt-6">Portrait</div>
                  <div className="font-display text-lg mt-1">{name}</div>
                  <div className="text-[var(--smoke)] text-xs mt-1">Collector · Community organizer</div>
                </div>
              )}
              <div className="absolute -top-4 -right-4 sticker sticker-red rotate-[8deg]">Collector</div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="kicker kicker-red">The short version</div>
            <h2 className="h-display text-4xl md:text-5xl mt-3">
              Gil finished his<br />last World Cup<br />album in 1998.
            </h2>
            <p className="mt-5 text-[var(--ink)] leading-relaxed">
              He was about eight years old, trading stickers at recess and
              celebrating every foil like a last-minute winner. Then life
              moved. In 2002 he moved to the United States. Sticker collecting
              wasn&apos;t part of school culture anymore, and slowly the hobby
              slipped out of his life.
            </p>
            <p className="mt-4 text-[var(--ink)] leading-relaxed">
              For twenty-eight years, the album stayed on a shelf.
            </p>
          </div>
        </div>
      </section>

      {/* The story — the return */}
      <section
        className="py-16 md:py-24 bg-[var(--panini-yellow-soft)] border-y-2 border-[var(--ink)]"
        data-testid="about-story"
      >
        <div className="container-gm grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="kicker kicker-red">The comeback</div>
            <h2 className="h-display text-4xl md:text-5xl mt-3">
              2026.<br />
              <span className="text-[var(--panini-red)]">This time,</span><br />
              no matter what.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-[var(--ink)] leading-relaxed">
            <p>
              When Panini announced the 2026 World Cup album, something clicked
              back into place. Gil decided he was going to finish it — no matter
              what.
            </p>
            <p>
              He started checking online every day. He drove past stores
              waiting for the release. He kept a running list of every shop
              that <em>might</em> carry it. When the album finally landed, he
              was already there — with an empty binder and a 50-pack box.
            </p>
            <p>
              His wife joined him at the kitchen table, sorting stickers into
              neat piles. It felt exactly like being eight years old again.
              Except this time, when he needed more packs, every store was
              already sold out.
            </p>
            <p>
              He called <strong>more than twenty stores</strong>. Nobody had
              inventory. So instead of waiting, he tracked down a distributor
              and bought inventory himself.
            </p>
          </div>
        </div>
      </section>

      {/* Your Phone Plug + first event */}
      <section className="container-gm py-16 md:py-24" data-testid="about-yourphoneplug">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 space-y-5 text-[var(--ink)] leading-relaxed">
            <div className="kicker kicker-red">While every store was sold out</div>
            <h2 className="h-display text-4xl md:text-5xl mt-2">
              Collectors could<br />always find stickers<br />
              <span className="text-[var(--field-green)]">at Your Phone Plug.</span>
            </h2>
            <p>
              What started as one collector trying to finish one album quickly
              turned into a small local rescue mission. Kids messaged. Parents
              messaged. Long-time collectors messaged. Everyone was looking for
              the same thing: <em>a real person with real stock and a fair
              price</em>.
            </p>
            <p>
              So Gil sent a message on Instagram: <strong>May 16 — first Sticker
              Trading Event.</strong> He expected fifteen or twenty people to
              show up. Somewhere around <strong>eighty collectors</strong> came
              through the door.
            </p>
            <p>
              That single Saturday became the beginning of the South Florida
              Panini community.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-3xl border-4 border-[var(--ink)] bg-[var(--ink)] text-white p-8 md:p-10 shadow-[10px_10px_0_var(--panini-yellow)] relative">
              <div className="flex items-center gap-3 text-[var(--panini-yellow)]">
                <Calendar size={18} />
                <span className="kicker kicker-yellow text-[10px]">May 16 · First Event</span>
              </div>
              <div className="h-display text-5xl md:text-6xl mt-4 text-[var(--panini-yellow)]">
                ~80
              </div>
              <div className="font-display text-lg mt-1 text-white">collectors showed up.</div>
              <p className="text-white/70 mt-4 text-sm leading-relaxed">
                Kids. Parents. Grandparents. Traders. First-timers. Standing
                room only. The room felt like a matchday.
              </p>
              <div className="absolute -top-4 -left-4 sticker sticker-red rotate-[-8deg]">Sold out</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission today */}
      <section
        className="py-16 md:py-24 bg-[var(--ink)] text-white"
        data-testid="about-vision"
      >
        <div className="container-gm grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="kicker kicker-yellow">Where we go from here</div>
            <h2 className="h-display text-4xl md:text-6xl mt-3 text-white">
              Finding albums, finding<br />
              <span className="text-[var(--panini-yellow)]">missing stickers,</span><br />
              bringing collectors together.
            </h2>
            <p className="mt-5 text-white/80 leading-relaxed max-w-lg">
              The mission today is exactly what it was on May 16: help
              collectors finish their albums, source the exact stickers they
              need, and turn every Saturday into a little bit of matchday.
              South Florida first. The rest of the country next.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/events" className="btn-yellow" data-testid="about-cta-events">Find the next event</Link>
              <Link to="/order-missing" className="btn-ghost text-white border-white/40" data-testid="about-cta-missing">
                Send us your missing list
              </Link>
              <Link to="/certificates" className="btn-ghost text-white border-white/40" data-testid="about-cta-registry">
                View certificates
              </Link>
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <VisionStat Icon={Users} label="Community" value="Growing" note="collectors, kids, families" />
              <VisionStat Icon={Trophy} label="Albums" value="On record" note="numbered & public" />
              <VisionStat Icon={MapPin} label="Events" value="Local" note="South Florida & beyond" />
              <VisionStat Icon={Flag} label="Coverage" value="50 states" note="+ D.C. shipping" />
            </div>
          </div>
        </div>
      </section>

      {/* Values / features restated */}
      <section className="container-gm py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <Feature
            color="yellow"
            Icon={ShieldCheck}
            kicker="Honest sourcing"
            title="No surprises."
            body="We confirm availability and pricing before you're invoiced. Nothing ships without your OK."
          />
          <Feature
            color="green"
            Icon={Trophy}
            kicker="Numbered records"
            title="GM-YYYY-XXXX"
            body="Every certificate is uniquely numbered and searchable in the public certificates, with your privacy preserved."
          />
          <Feature
            color="red"
            Icon={Compass}
            kicker="Community first"
            title="Come as you are."
            body="Kids, parents, veteran collectors — everyone's welcome at our Trading Events."
          />
        </div>

        <div className="mt-14 border-l-4 border-[var(--panini-red)] pl-6 md:pl-8 py-4">
          <div className="kicker kicker-red">Important note</div>
          <p className="text-[var(--ink)] mt-3 leading-relaxed max-w-3xl">
            {settings.disclaimer_footer}
          </p>
        </div>
      </section>
    </div>
  );
}

/* -------- helpers (unchanged) -------- */

function Pillar({ Icon, color, title, body }) {
  const map = {
    yellow: "bg-[var(--panini-yellow)] text-[var(--ink)]",
    green: "bg-[var(--field-green)] text-white",
    red: "bg-[var(--panini-red)] text-white",
  };
  return (
    <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6">
      <div className={`w-12 h-12 rounded-xl border-2 border-[var(--ink)] flex items-center justify-center ${map[color]}`}>
        <Icon size={22} />
      </div>
      <h3 className="font-display text-xl mt-4 leading-tight">{title}</h3>
      <p className="text-[var(--smoke)] mt-2 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function VisionStat({ Icon, label, value, note }) {
  return (
    <div className="rounded-2xl border-2 border-white/25 bg-white/5 p-5">
      <div className="w-10 h-10 rounded-xl border-2 border-white/30 flex items-center justify-center text-[var(--panini-yellow)]">
        <Icon size={18} />
      </div>
      <div className="font-display text-2xl text-white mt-4">{value}</div>
      <div className="kicker kicker-yellow text-[10px] mt-1">{label}</div>
      <div className="text-white/50 text-xs mt-1">{note}</div>
    </div>
  );
}

function Feature({ Icon, color, kicker, title, body }) {
  const map = {
    yellow: "bg-[var(--panini-yellow)] text-[var(--ink)]",
    green: "bg-[var(--field-green)] text-white",
    red: "bg-[var(--panini-red)] text-white",
  };
  return (
    <div className="card-fun p-8">
      <div className={`w-14 h-14 rounded-2xl border-2 border-[var(--ink)] flex items-center justify-center ${map[color]}`}>
        <Icon size={26} />
      </div>
      <div className="kicker kicker-red mt-6">{kicker}</div>
      <h3 className="font-display text-3xl mt-2">{title}</h3>
      <p className="text-[var(--smoke)] mt-3 leading-relaxed text-sm">{body}</p>
    </div>
  );
}

// Heart is imported for a future re-add of the three-pillar section.
// Marked as used to keep it available for the owner if they want to re-enable.
export const _reservedIcons = { Heart, Sparkles };
